import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';

import { Button, Input, Select, Form, Alert } from 'antd';
import moment from 'moment';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';
import { ProviderType, UserRole } from 'utils/enums';
import { selectDefaultProvider } from 'utils/helpers';

import { getAuthListGroups } from 'store/authorization/list/actions';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import { SearchAuthFormProps } from './Proptypes';

import './index.scss';

const { Option } = Select;

const SearchAuthForm = (props: SearchAuthFormProps) => {
  const ALL_PROVIDERS = { Provider: "ALL PROVIDERS", RmiProvKeyId: null, RmiCompanyId: null };
  const [provider, setProvider] = useState(JSON.stringify(ALL_PROVIDERS));
  const [birth, setBirth] = useState('');
  const initAuthStatus = '{"Code": null}';
  const [authStatus, setAuthStatus] = useState(initAuthStatus);

  const [form] = Form.useForm();
  const [showHelpfulText, setShowHelpfulText] = useState(false);

  //sets the provider dropdown to the login user as default provider
  useEffect(() => {
    if (props.providerList.length > 0) {

     const defaultProvider = selectDefaultProvider(props.providerList, JSON.stringify(ALL_PROVIDERS), 'string');

      setProvider(defaultProvider)
      form.setFieldsValue({provider: defaultProvider})
    }
  }, [props.providerList]);

  useEffect(() => {
    birth !== '' && form.validateFields();
  }, [birth]);

  const parseAuthStatus = (authStatus: string) => {
    if (authStatus) {
      return JSON.parse(authStatus);
    }
    return null;
  };

  const validationRules = async (fieldData: any, value: any) => {
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    const allEmptyFields = () => {
      return !form.getFieldValue('memberId') && !birth &&
        !form.getFieldValue('firstName') && !form.getFieldValue('lastName') &&
        !form.getFieldValue('authNo') &&
        !form.getFieldValue('authStatus');
    }

    const allFieldsResolved = () => {
      return (form.getFieldValue('memberId') && birth) ||
        (form.getFieldValue('firstName') && form.getFieldValue('lastName') && birth) ||
        form.getFieldValue('authNo') ||
        form.getFieldValue('authStatus');
    }

    if (charLessThanFive(value)) {
      return Promise.reject('Please enter at least 5 characters.');
    }

    if (allEmptyFields()) {
      setShowHelpfulText(true);
      return Promise.reject('Required');
    }

    if (allFieldsResolved()) {
      setShowHelpfulText(false);
      return Promise.resolve();
    }

    if (JSON.parse(provider || '{}').Provider === 'ALL PROVIDERS' || ![ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass)) {

      if (fieldData.field === 'memberId') {
        const memberIdResolved = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || (form.getFieldValue('firstName') && form.getFieldValue('lastName'));
        }

        if (memberIdResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (birth) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'birth') {
        const DOBResolved = () => {
          return birth || (form.getFieldValue('authStatus') || form.getFieldValue('authNo'));
        }

        const atLeastOne = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('firstName') || form.getFieldValue('lastName'));
        }

        if (DOBResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (atLeastOne()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'authNo') {
        const authNoResolved = () => {
          return form.getFieldValue('authNo') || birth;
        }

        if (authNoResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'authStatus') {
        const authStatusResolved = () => {
          return form.getFieldValue('authStatus') || birth;
        }

        if (authStatusResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstName') {
        const firstNameResolved = () => {
          return (form.getFieldValue('firstName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastName'));
        }

        const missingDOB = () => {
          return form.getFieldValue('lastName') && !birth;
        }

        if (firstNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDOB()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastName') {
        const lastNameResolved = () => {
          return (form.getFieldValue('lastName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstName'));
        }

        const missingDOB = () => {
          return form.getFieldValue('firstName') && !birth;
        }

        if (lastNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDOB()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    } else {

      // PCP (P) / Clinic (C) validation
      if (fieldData.field === 'memberId') {
        const memberIdResolved = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo'))
            || (form.getFieldValue('firstName') && form.getFieldValue('lastName'));
        }

        if (memberIdResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'birth') {
        const dobResolved = () => {
          return birth || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId');
        }

        const missingDob = () => {
          return (form.getFieldValue('firstName') || form.getFieldValue('lastName'));
        }

        if (dobResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'authNo') {
        const authNoResolved = () => {
          return form.getFieldValue('authNo') || birth;
        }

        if (authNoResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'authStatus') {
        const authStatusResolved = () => {
          return form.getFieldValue('authStatus') || birth;
        }

        if (authStatusResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstName') {
        const firstNameResolved = () => {
          return (form.getFieldValue('firstName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastName'));
        }

        const missingDob = () => {
          return form.getFieldValue('lastName') && !birth;
        }

        if (firstNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastName') {
        const lastNameResolved = () => {
          return (form.getFieldValue('lastName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstName'));
        }

        const missingDob = () => {
          return form.getFieldValue('firstName') && !birth;
        }

        if (lastNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    }
  };

  const onSearch = () => {
    form.validateFields().then(values => {
      let form_data = {
        "AuthSearchModel": {
          "AuthNo": values.authNo,
          "AuthStatus": parseAuthStatus(authStatus).Code,
          "CompanyId": parseAuthStatus(provider).RmiCompanyId,
          "DOB": birth === '' ? null : birth,
          "FirstName": values.firstName,
          "LastName": values.lastName,
          "MembId": values.memberId,
          "ProvKeyId": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? JSON.parse(provider).RmiProvKeyId : undefined,
          "Provider": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? JSON.parse(provider) : {},
          "ProviderName": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? JSON.parse(provider).Provider : undefined,
          "Status": parseAuthStatus(authStatus),
          "index": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? 0 : undefined,
          "statusIndex": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? -1: undefined,
        },
        "UserIdModel": {UserId: sessionStorage.user_id, UserRole: sessionStorage.user_role}
      };

      localStorage.setItem("print_ipa", parseAuthStatus(provider).RmiCompanyId);

      return sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ?
        props.getAuthList(form_data)
          .then((response: any[]) => {
            if (response.length === 0) {
              notifier.error(' There were no results for this search. Please check the fields and try again.');
              return;
            }
            props.newTableDataHandler(response);
          })
          .catch((err: ErrorResponse) => {
              notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
          })
          :
        props.getAuthListGroups(form_data)
          .then((response: any[]) => {
            if (response.length === 0) {
              notifier.error(' There were no results for this search. Please check the fields and try again.');
              return;
            }
            props.newTableDataHandler(response);
          })
          .catch((err: ErrorResponse) => {
              notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
          });
    });
  };

  const onReset = () => {
    form.resetFields();
    setBirth('');
    setAuthStatus(initAuthStatus);
    setProvider(JSON.stringify({ Provider: "ALL PROVIDERS", RmiProvKeyId: null, RmiCompanyId: null }));
    setShowHelpfulText(false);
    props.newTableDataHandler([]);
  };

  return (
    <Form form={form} name={'search-auth-form'} onFinish={onSearch}>
      <div className="row">
        {sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN &&
          <div className="col-lg-6 col-md-4">
            <FloatLabel
              label="Provider:"
              name="provider"
              form={form}
              value={provider}
            >
              <Select
                showSearch
                value={provider}
                placeholder="Select a Provider..."
                onChange={value => setProvider(value)}
                loading={props.isFetchingAuthList}
              >
                <Option key={'all'} value={JSON.stringify({ Provider: "ALL PROVIDERS", RmiProvKeyId: null, RmiCompanyId: null })}>ALL PROVIDERS</Option>
                {props.providerList.map((option: any, key: number) => {
                  return <Option key={key} value={JSON.stringify(option)}>{option.Provider}</Option>;
                })}
              </Select>
            </FloatLabel>
          </div>
        }

        <div className="col-lg-6 col-md-4">
            <FloatLabel
              label="Auth No"
              name="authNo"
              form={form}
              dependencies={['firstName', 'lastName', 'memberId', 'authStatus']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
            <Input />
            </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Status:"
            name="authStatus"
            form={form}
            dependencies={['firstName', 'lastName', 'memberId', 'authNo']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
            <Select
              value={authStatus}
              onChange={value => setAuthStatus(value)}
            >
              <Option value='{"Code": "1", "Descr": "Approved"}'>APPROVED</Option>
              <Option value='{"Code": "2", "Descr": "Modified"}'>MODIFIED</Option>
              <Option value='{"Code": "3", "Descr": "Denied"}'>DENIED</Option>
              <Option value='{"Code": "4", "Descr": "Deferred"}'>DEFERRED</Option>
              <Option value='{"Code": "5", "Descr": "Expired"}'>EXPIRED</Option>
              <Option value='{"Code": "6", "Descr": "Cancelled"}'>CANCELLED</Option>
              <Option value='{"Code": "7", "Descr": "Requested"}'>REQUESTED</Option>
              <Option value='{"Code": "8", "Descr": "System Hold"}'>SYSTEM HOLD</Option>
              <Option value='{"Code": "9", "Descr": "Pended"}'>PENDED</Option>
            </Select>
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Member ID:"
            name="memberId"
            form={form}
            dependencies={['firstName', 'lastName', 'authStatus', 'authNo']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        {sessionStorage.getItem('user_role') === UserRole.HEALTH_PLAN && <div className="col-lg-6" />}

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Last Name"
            name="lastName"
            form={form}
            dependencies={['firstName', 'authStatus', 'memberId', 'authNo']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="First Name"
            name="firstName"
            form={form}
            dependencies={['authStatus', 'lastName', 'memberId', 'authNo']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="DOB (MM/DD/YYYY)"
            value={birth}
            form={form}
            name="birth"
            dependencies={['firstName', 'lastName', 'authStatus', 'memberId', 'authNo']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              }),
              () => ({
                async validator() {
                  const validDate = () => birth && moment(birth).isValid() && moment(birth).year() >= 1900 && birth.match(/^\d{2}\/\d{2}\/\d{4}$/);
                  if (validDate()) {
                    return Promise.resolve();
                  }

                  if (!showHelpfulText && birth) {
                    return Promise.reject(new Error('Invalid Date'));
                  }
                }
              })
            ]}
          >
          <DatePickerLocalised
            date={birth}
            setDate={setBirth}
          />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4 btn-wrapper mb-4">
          <Button type="primary" style={{ width: '100%' }} htmlType={'submit'} loading={props.isFetchingList || props.isFetchingListGroup}>SEARCH</Button>
          <Button type="default" style={{ width: '100%' }} onClick={onReset}>RESET</Button>
        </div>
      </div>

      {(showHelpfulText && (JSON.parse(provider || '{}' ).Provider === 'ALL PROVIDERS' || ![ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass))) ? (
        <Alert
          message={
            <>
              Please meet one of the following requirements:<br />
              MemberID + DOB (mm/dd/yyyy) <br />
              Auth Number ONLY <br />
              Status ONLY <br />
              Last Name + First Name + DOB (mm/dd/yyyy)
            </>
          }
          type="error"
          className="col-lg-24"
        />
      ) : null }

      {(showHelpfulText && [ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}' ).RmiClass)) ? (
        <Alert
          message={
            <>
              Please meet one of the following requirements:<br />
              MemberID ONLY <br />
              Auth Number ONLY <br />
              Status ONLY <br />
              Last Name + First Name ONLY
            </>
          }
          type="error"
          className="col-lg-24"
        />
      ) : null }

    </Form>
  );
}
const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingListGroup: store.rootReducer.authList.isFetchingListGroup,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
{
  getAuthListGroups
},
dispatch,
);
export default connect(mapStateToProps, mapDispatchToProps)(SearchAuthForm);